// Radha — Zoho Books integration surface, in Settings.
//
// The previous version was one button and a five-line log, which meant the
// answer to "is this working?" was "press it and see". That conflates four
// different failures — no credentials, revoked credentials, wrong data centre,
// wrong organisation — each needing a different fix. So the panel now leads
// with connection state, names the organisation it is actually wired to, shows
// how much of the book came from Zoho, and states plainly which fields cross
// the boundary and which do not.
import React, { useState } from "react";

import {
  useErpSyncLog, useSyncZohoInvoices, useZohoStatus, useZohoCoverage,
  zohoErrorMessage, type ZohoStatus, type ErpSyncLog,
} from "../hooks/useZohoSync";
import { fmtDate, fmtDateTime } from "../lib/money";

// Zoho Books field -> Radha column. Written out because "it syncs invoices" is
// not a specification, and the gaps below are the part people get surprised by.
const FIELD_MAP: Array<[string, string, string]> = [
  ["invoice_number", "invoice_number", "Falls back to Zoho's invoice_id if blank"],
  ["customer_name", "customer_name", "“Unknown” if absent"],
  ["gst_no", "customer_gstin", "Drives the B2B/B2C and intra/inter-state split"],
  ["total − tax_total", "taxable_amount", "Derived; Zoho sends the gross"],
  ["tax_total", "tax_amount", "Single total, not a CGST/SGST/IGST breakdown"],
  ["total − balance", "amount_paid", "Derived, so partial receipts carry over"],
  ["status", "status", "Mapped; partially_paid stays overdue until fully covered"],
  ["date / due_date", "issue_date / due_date", "due_date falls back to date"],
  ["invoice_id", "zoho_invoice_id", "The idempotency key — re-syncs update, never duplicate"],
];

const NOT_SYNCED = [
  "Line items — Zoho's list endpoint omits them; pulling them needs one API call per invoice.",
  "GST component breakdown — only the tax total crosses; the tax tab derives the split from GSTINs.",
  "Credit notes, payments recorded in Zoho, and customer master data.",
  "Nothing is written back except confirmed payments, pushed one invoice at a time from Invoicing.",
];

const STATE_UI: Record<ZohoStatus["state"], { label: string; color: string }> = {
  connected: { label: "Connected", color: "var(--status-good)" },
  not_configured: { label: "Not configured", color: "var(--fg-muted)" },
  auth_failed: { label: "Credentials rejected", color: "var(--status-critical)" },
  api_failed: { label: "Zoho API unreachable", color: "var(--status-critical)" },
  org_mismatch: { label: "Wrong organisation", color: "var(--status-warning)" },
};

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "7px 8px", verticalAlign: "top" };

function Dot({ ok }: { ok: boolean }) {
  return (
    <span aria-hidden style={{
      display: "inline-block", width: 7, height: 7, borderRadius: "50%", marginInlineEnd: 7,
      background: ok ? "var(--status-good)" : "var(--fg-faint)",
    }} />
  );
}

function duration(l: ErpSyncLog): string {
  if (!l.completed_at) return "—";
  const ms = new Date(l.completed_at).getTime() - new Date(l.created_at).getTime();
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function Connection({ status, loading }: { status?: ZohoStatus; loading: boolean }) {
  if (loading) return <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>Checking connection…</p>;
  if (!status) return <p style={{ color: "var(--status-critical)", fontSize: 13 }}>Status check unavailable.</p>;
  const ui = STATE_UI[status.state] ?? { label: status.state, color: "var(--fg-muted)" };

  return (
    <div className="sanctuary-card" style={{ padding: "12px 14px", marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ font: "600 13px Inter", color: ui.color }}>● {ui.label}</span>
        {status.org && (
          <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>
            {status.org.name} · org {status.org.id}
            {status.org.currency && ` · ${status.org.currency}`}
          </span>
        )}
        <span style={{ marginInlineStart: "auto", fontSize: 11.5, color: "var(--fg-faint)" }}>
          {status.api_domain}
        </span>
      </div>

      {status.error && (
        <p style={{ fontSize: 12.5, color: "var(--status-critical)", margin: "8px 0 0" }}>
          {status.error}
          {status.hint && <span style={{ display: "block", color: "var(--fg-muted)", marginTop: 3 }}>{status.hint}</span>}
        </p>
      )}

      {status.visible_orgs && status.visible_orgs.length > 0 && (
        <p style={{ fontSize: 12, color: "var(--fg-muted)", margin: "6px 0 0" }}>
          This token can see: {status.visible_orgs.map((o) => `${o.name} (${o.id})`).join(", ")}
        </p>
      )}

      {status.state === "not_configured" && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12.5, color: "var(--fg-muted)", marginBottom: 6 }}>
            Credentials required — create a Self Client in the Zoho API console, then set them as Supabase secrets.
            Values are never read back into this page.
          </div>
          <div style={{ display: "grid", gap: 3, marginBottom: 8 }}>
            {Object.entries(status.secrets).map(([k, present]) => (
              <div key={k} style={{ fontSize: 12, color: present ? "var(--fg)" : "var(--fg-muted)" }}>
                <Dot ok={present} />{k}{present ? "" : " — not set"}
              </div>
            ))}
          </div>
          <code style={{
            display: "block", fontSize: 11, padding: "7px 9px", borderRadius: 7,
            background: "var(--surface-2)", color: "var(--fg-muted)", overflowX: "auto", whiteSpace: "pre",
          }}>
            {`supabase secrets set --project-ref <ref> \\\n  ${status.missing.join("=... \\\n  ")}=...`}
          </code>
          <p style={{ fontSize: 11.5, color: "var(--fg-faint)", margin: "6px 0 0" }}>
            Data centre is set to {status.accounts_domain}. If the Zoho account is on the .com region, also set
            ZOHO_ACCOUNTS_DOMAIN and ZOHO_API_DOMAIN.
          </p>
        </div>
      )}
    </div>
  );
}

export function ErpSync({ canWrite }: { canWrite: boolean }) {
  const { data: status, isLoading: statusLoading } = useZohoStatus();
  const { data: log, isLoading } = useErpSyncLog();
  const { data: coverage } = useZohoCoverage();
  const sync = useSyncZohoInvoices();
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [openErr, setOpenErr] = useState<string | null>(null);

  const run = () => {
    setNote(null);
    sync.mutate(undefined, {
      onSuccess: (r) => setNote({ ok: true, text: `Synced ${r.synced} of ${r.total_seen} invoices` }),
      onError: (e) => setNote({ ok: false, text: zohoErrorMessage(e) }),
    });
  };

  const pulls = (log ?? []).filter((l) => l.direction === "pull_invoices");
  const pushes = (log ?? []).filter((l) => l.direction === "push_payment");
  const pct = coverage && coverage.total > 0 ? Math.round((coverage.linked / coverage.total) * 100) : 0;
  const blocked = status?.state === "not_configured";

  return (
    <div style={{ maxWidth: 760, marginTop: 32 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 6 }}>ERP sync — Zoho Books</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "0 0 14px" }}>
        Zoho Books stays your system of record for invoicing and GST filing; this pulls its invoices into Radha so
        Collections, Dunning and Reconciliation can operate on them. Confirmed payments are pushed back per invoice
        from the Invoicing tab.
      </p>

      <Connection status={status} loading={statusLoading} />

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", margin: "0 0 16px" }}>
        <div title="Invoices carrying a zoho_invoice_id — those that originated in, or are matched to, Zoho Books.">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Invoices from Zoho</div>
          <div style={{ font: "600 19px Inter" }}>
            {coverage ? `${coverage.linked} / ${coverage.total}` : "—"}
            <span style={{ fontSize: 12.5, fontWeight: 400, color: "var(--fg-muted)" }}> · {pct}%</span>
          </div>
        </div>
        <div>
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Last pull</div>
          <div style={{ font: "600 19px Inter" }}>
            {pulls[0] ? fmtDate(pulls[0].created_at) : "Never"}
          </div>
        </div>
        <div>
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Payments pushed</div>
          <div style={{ font: "600 19px Inter" }}>{pushes.filter((p) => p.status === "completed").length}</div>
        </div>
      </div>

      {canWrite && (
        <button onClick={run} disabled={sync.isPending || blocked}
          title={blocked ? "Set the Zoho credentials first" : undefined}
          style={{
            padding: "7px 14px", border: 0, borderRadius: 8, fontSize: 13,
            background: blocked ? "var(--surface-2)" : "var(--accent-purple)",
            color: blocked ? "var(--fg-faint)" : "#fff",
            cursor: blocked ? "not-allowed" : "pointer",
          }}>
          {sync.isPending ? "Syncing…" : "Sync invoices now"}
        </button>
      )}
      {note && <p style={{ fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)", marginTop: 8 }}>{note.text}</p>}

      <div style={{ font: "600 13px Inter", margin: "24px 0 8px" }}>Sync history</div>
      {isLoading ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>Loading…</p>
      ) : (log ?? []).length === 0 ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>No syncs yet.</p>
      ) : (
        <div className="sanctuary-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
                <th style={th}>When</th><th style={th}>Direction</th><th style={th}>Result</th>
                <th style={{ ...th, textAlign: "right" }}>Records</th>
                <th style={{ ...th, textAlign: "right" }}>Took</th>
              </tr>
            </thead>
            <tbody>
              {(log ?? []).map((l) => {
                const failed = l.status === "failed";
                return (
                  <React.Fragment key={l.id}>
                    <tr style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={{ ...td, color: "var(--fg-muted)", whiteSpace: "nowrap" }}>
                        {fmtDateTime(l.created_at)}
                      </td>
                      <td style={{ ...td, textTransform: "capitalize" }}>{l.direction.replace(/_/g, " ")}</td>
                      <td style={{ ...td, color: failed ? "var(--status-critical)" : l.status === "running" ? "var(--status-warning)" : "var(--status-good)" }}>
                        {l.status}
                        {failed && l.error_detail && (
                          <button onClick={() => setOpenErr(openErr === l.id ? null : l.id)}
                            style={{ marginInlineStart: 6, padding: "0 5px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
                            {openErr === l.id ? "hide" : "why"}
                          </button>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>{l.records_processed}</td>
                      <td style={{ ...td, textAlign: "right", color: "var(--fg-faint)" }}>{duration(l)}</td>
                    </tr>
                    {openErr === l.id && l.error_detail && (
                      <tr>
                        <td colSpan={5} style={{ ...td, background: "var(--surface-2)", color: "var(--status-critical)", fontSize: 11.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {l.error_detail}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ font: "600 13px Inter", margin: "26px 0 8px" }}>What crosses the boundary</div>
      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
              <th style={th}>Zoho Books</th><th style={th}>Radha</th><th style={th}>Note</th>
            </tr>
          </thead>
          <tbody>
            {FIELD_MAP.map(([from, to, note]) => (
              <tr key={to} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontSize: 11.5 }}>{from}</td>
                <td style={{ ...td, fontFamily: "ui-monospace, monospace", fontSize: 11.5 }}>{to}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ font: "600 13px Inter", margin: "20px 0 6px" }}>What does not</div>
      <ul style={{ margin: 0, paddingInlineStart: 18, color: "var(--fg-muted)", fontSize: 12.5, lineHeight: 1.65 }}>
        {NOT_SYNCED.map((s) => <li key={s}>{s}</li>)}
      </ul>
    </div>
  );
}
